import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faCrown, faRocket, faBuilding, faBolt } from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Button } from '../components/common/Button';

interface PlanFeature { text: string; included: boolean; }
interface Plan { name: string; icon: typeof faCrown; price: string; period: string; description: string; features: PlanFeature[]; cta: string; popular?: boolean; color: string; }

const PLANS: Plan[] = [
  {
    name: '무료',
    icon: faBolt,
    price: '0',
    period: '영구 무료',
    description: '시작하는 개인 사용자를 위한 플랜',
    color: 'var(--color-text-tertiary)',
    cta: '시작하기',
    features: [
      { text: '최대 3개 딜 관리', included: true },
      { text: '팀원 3명', included: true },
      { text: '1 GB 저장공간', included: true },
      { text: '템플릿 5개', included: true },
      { text: '기본 분석', included: true },
      { text: '문서 열람 추적', included: false },
      { text: '커스텀 브랜딩', included: false },
      { text: 'API 접근', included: false },
      { text: '우선 지원', included: false },
    ],
  },
  {
    name: '스타터',
    icon: faRocket,
    price: '29,000',
    period: '/사용자/월',
    description: '성장하는 영업팀을 위한 플랜',
    color: 'var(--color-info)',
    cta: '무료 체험',
    features: [
      { text: '최대 25개 딜 관리', included: true },
      { text: '팀원 10명', included: true },
      { text: '10 GB 저장공간', included: true },
      { text: '무제한 템플릿', included: true },
      { text: '고급 분석', included: true },
      { text: '문서 열람 추적', included: true },
      { text: '커스텀 브랜딩', included: false },
      { text: 'API 접근', included: false },
      { text: '우선 지원', included: false },
    ],
  },
  {
    name: '프로페셔널',
    icon: faCrown,
    price: '79,000',
    period: '/사용자/월',
    description: '확장하는 조직을 위한 플랜',
    color: 'var(--color-accent)',
    cta: '무료 체험',
    popular: true,
    features: [
      { text: '무제한 딜', included: true },
      { text: '무제한 팀원', included: true },
      { text: '100 GB 저장공간', included: true },
      { text: '무제한 템플릿', included: true },
      { text: '고급 분석', included: true },
      { text: '문서 추적 + NDA', included: true },
      { text: '커스텀 브랜딩', included: true },
      { text: 'API 접근', included: true },
      { text: '우선 지원', included: false },
    ],
  },
  {
    name: '엔터프라이즈',
    icon: faBuilding,
    price: '문의',
    period: '',
    description: '대규모 조직을 위한 맞춤 플랜',
    color: 'var(--color-warning)',
    cta: '영업팀 문의',
    features: [
      { text: '프로페셔널 전체 포함', included: true },
      { text: '모든 기능 무제한', included: true },
      { text: '무제한 저장공간', included: true },
      { text: 'SSO / SAML', included: true },
      { text: '맞춤 연동', included: true },
      { text: '감사 로그', included: true },
      { text: '전담 CSM', included: true },
      { text: 'SLA 보장', included: true },
      { text: '24/7 우선 지원', included: true },
    ],
  },
];

export function Pricing() {
  return (
    <main id="main-content" role="main" style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-16)' }}>
      <div className="container">
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-12)' }}>
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: 'var(--space-4)' }}>
              심플하고 투명한 요금제
            </h1>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-lg)', maxWidth: '560px', margin: '0 auto' }}>
              팀에 맞는 플랜을 선택하세요. 언제든지 업그레이드하거나 다운그레이드할 수 있습니다.
            </p>
          </div>
        </FadeUp>

        <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-4)', alignItems: 'start' }}>
          {PLANS.map((plan, i) => (
            <FadeUp key={plan.name} delay={i * 0.08}>
              <GlassCard style={{ position: 'relative', border: plan.popular ? `2px solid ${plan.color}` : undefined, height: '100%' }}>
                {plan.popular && (
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', padding: 'var(--space-1) var(--space-4)', borderRadius: 'var(--radius-full)', background: plan.color, color: 'white', fontSize: 'var(--text-xs)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    가장 인기
                  </div>
                )}
                <div style={{ textAlign: 'center', marginBottom: 'var(--space-6)' }}>
                  <div style={{ width: '3rem', height: '3rem', borderRadius: 'var(--radius-lg)', margin: '0 auto var(--space-3)', background: `color-mix(in oklch, ${plan.color} 12%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FontAwesomeIcon icon={plan.icon} style={{ color: plan.color, fontSize: 'var(--text-xl)' }} />
                  </div>
                  <h3 style={{ fontSize: 'var(--text-xl)', fontFamily: 'var(--font-display)', marginBottom: 'var(--space-1)' }}>{plan.name}</h3>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-4)' }}>{plan.description}</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 'var(--space-1)' }}>
                    {plan.price !== '문의' && <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>{'\u20A9'}</span>}
                    <span style={{ fontSize: 'var(--text-4xl)', fontWeight: 800, fontFamily: 'var(--font-display)' }}>{plan.price}</span>
                    {plan.period && <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>{plan.period}</span>}
                  </div>
                </div>
                <div style={{ marginBottom: 'var(--space-6)' }}>
                  {plan.features.map(f => (
                    <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) 0', fontSize: 'var(--text-sm)' }}>
                      <FontAwesomeIcon icon={f.included ? faCheck : faXmark} style={{ color: f.included ? 'var(--color-success)' : 'var(--color-text-tertiary)', width: '14px', flexShrink: 0 }} />
                      <span style={{ color: f.included ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)' }}>{f.text}</span>
                    </div>
                  ))}
                </div>
                <Button variant={plan.popular ? 'primary' : 'secondary'} size="lg" style={{ width: '100%', justifyContent: 'center' }}>
                  {plan.cta}
                </Button>
              </GlassCard>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.4}>
          <div style={{ textAlign: 'center', marginTop: 'var(--space-12)' }}>
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
              모든 플랜에 14일 무료 체험이 포함됩니다. 신용카드 없이 시작하세요.
            </p>
          </div>
        </FadeUp>
      </div>

      <style>{`
        @media (min-width: 768px) { .pricing-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (min-width: 1024px) { .pricing-grid { grid-template-columns: repeat(4, 1fr) !important; } }
      `}</style>
    </main>
  );
}
